import { useState } from 'react';
import { useSitemapScan, useSitemapSources } from '../../hooks/useSitemaps';
import { useSources, useUpdateSource, useCreateSource } from '../../hooks/useSources';
import { useBlueskyFeeds, DEFAULT_NEWS_HANDLES } from '../../hooks/useBluesky';
import { ExternalLink, Check, Clock, AlertCircle, RefreshCw, Settings, Heart, MessageCircle, Repeat2 } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerTrigger,
} from '../ui/drawer';

type Tab = 'sitemaps' | 'bluesky';

export function SitemapsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('sitemaps');

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">News Sources</h1>
        <p className="text-gray-600 text-sm mt-1">
          Discover articles from sitemaps, RSS feeds, and social media
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('sitemaps')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'sitemaps'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Sitemaps & RSS
        </button>
        <button
          onClick={() => setActiveTab('bluesky')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'bluesky'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Bluesky
        </button>
      </div>

      {activeTab === 'sitemaps' ? <SitemapsTab /> : <BlueskyTab />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sitemaps Tab
// ─────────────────────────────────────────────────────────────────────────────

function SitemapsTab() {
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [newOnly, setNewOnly] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: sourcesData } = useSitemapSources();
  const { data: scanData, isLoading, isError, error, refetch, isFetching } = useSitemapScan({
    source: selectedSource || undefined,
    newOnly,
    limit: 100,
  });

  const sources = sourcesData?.sources || [];
  const entries = scanData?.entries || [];
  const errors = scanData?.errors || [];

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

      if (diffMins < 60) {
        return `${diffMins}m ago`;
      } else if (diffHours < 24) {
        return `${diffHours}h ago`;
      } else {
        return date.toLocaleDateString();
      }
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
            <DrawerTrigger asChild>
              <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                <Settings className="w-4 h-4" />
                Configure Sources
              </button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>Configure Sitemap URLs</DrawerTitle>
                <DrawerDescription>
                  Add sitemap or RSS feed URLs to your sources to enable scanning.
                </DrawerDescription>
              </DrawerHeader>
              <div className="mt-4 overflow-y-auto max-h-[calc(100vh-200px)]">
                <SourcesEditor onClose={() => setDrawerOpen(false)} />
              </div>
            </DrawerContent>
          </Drawer>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Source:</span>
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Sources</option>
            {sources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={newOnly}
              onChange={(e) => setNewOnly(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">New articles only</span>
          </label>
        </div>

        <div className="text-sm text-gray-500 ml-auto">
          {scanData ? (
            <>
              {scanData.new_count} new / {scanData.total} total
              {scanData.scanned_at && (
                <span className="ml-2 text-gray-400">
                  scanned {formatDate(scanData.scanned_at)}
                </span>
              )}
            </>
          ) : null}
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2 text-yellow-800 font-medium text-sm mb-2">
            <AlertCircle className="w-4 h-4" />
            Some sources had errors
          </div>
          <ul className="text-sm text-yellow-700 space-y-1">
            {errors.map((err, i) => (
              <li key={i}>
                {err.source}: {err.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="text-gray-500 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 animate-spin" />
            Scanning sitemaps...
          </div>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error: {error.message}
        </div>
      )}

      {/* Feed items */}
      {!isLoading && entries.length > 0 && (
        <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
          {entries.map((entry) => (
            <div
              key={entry.url}
              className={`p-4 hover:bg-gray-50 transition-colors ${
                !entry.isNew ? 'opacity-60' : ''
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                    {entry.source.name}
                  </span>
                  {entry.isNew ? (
                    <span className="text-xs font-medium px-2 py-0.5 bg-green-100 text-green-700 rounded flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      New
                    </span>
                  ) : (
                    <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 text-gray-500 rounded flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Imported
                    </span>
                  )}
                  {entry.pubDate && (
                    <span className="text-xs text-gray-500">
                      {formatDate(entry.pubDate)}
                    </span>
                  )}
                </div>

                <a
                  href={entry.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium line-clamp-2"
                >
                  {entry.title || entry.url}
                  <ExternalLink className="w-3 h-3 inline-block ml-1 opacity-50" />
                </a>

                {entry.title && (
                  <div className="text-xs text-gray-400 truncate mt-1">
                    {entry.url}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && entries.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg shadow">
          <p>No articles found in sitemaps.</p>
          <p className="text-sm mt-2">
            {sources.length === 0
              ? 'Click "Configure Sources" to add sitemap URLs to your sources.'
              : newOnly
              ? 'Try unchecking "New articles only" to see all entries.'
              : 'Sources may not have sitemap URLs configured.'}
          </p>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bluesky Tab
// ─────────────────────────────────────────────────────────────────────────────

function BlueskyTab() {
  const [handles, setHandles] = useState<string[]>(DEFAULT_NEWS_HANDLES.slice(0, 5));
  const [customHandle, setCustomHandle] = useState('');
  const { data, isLoading, isError, error, refetch, isFetching } = useBlueskyFeeds(handles, 15);

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

      if (diffMins < 60) {
        return `${diffMins}m ago`;
      } else if (diffHours < 24) {
        return `${diffHours}h ago`;
      } else {
        return date.toLocaleDateString();
      }
    } catch {
      return dateStr;
    }
  };

  const toggleHandle = (handle: string) => {
    setHandles((prev) =>
      prev.includes(handle) ? prev.filter((h) => h !== handle) : [...prev, handle]
    );
  };

  const addCustomHandle = () => {
    if (customHandle && !handles.includes(customHandle)) {
      setHandles((prev) => [...prev, customHandle]);
      setCustomHandle('');
    }
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Add handle (e.g., nytimes.com)"
            value={customHandle}
            onChange={(e) => setCustomHandle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCustomHandle()}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 w-64"
          />
          <button
            onClick={addCustomHandle}
            disabled={!customHandle}
            className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            Add
          </button>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Handle chips */}
      <div className="mb-4 flex flex-wrap gap-2">
        {DEFAULT_NEWS_HANDLES.map((handle) => (
          <button
            key={handle}
            onClick={() => toggleHandle(handle)}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              handles.includes(handle)
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-gray-100 text-gray-500 border border-gray-200 hover:bg-gray-200'
            }`}
          >
            @{handle}
          </button>
        ))}
        {handles
          .filter((h) => !DEFAULT_NEWS_HANDLES.includes(h))
          .map((handle) => (
            <button
              key={handle}
              onClick={() => toggleHandle(handle)}
              className="px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-700 border border-blue-300"
            >
              @{handle} ×
            </button>
          ))}
      </div>

      {/* Errors */}
      {data?.errors && data.errors.length > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2 text-yellow-800 font-medium text-sm mb-2">
            <AlertCircle className="w-4 h-4" />
            Some accounts had errors
          </div>
          <ul className="text-sm text-yellow-700 space-y-1">
            {data.errors.map((err, i) => (
              <li key={i}>
                @{err.handle}: {err.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="text-gray-500 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 animate-spin" />
            Fetching Bluesky posts...
          </div>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error: {error.message}
        </div>
      )}

      {/* Posts */}
      {!isLoading && data?.posts && data.posts.length > 0 && (
        <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
          {data.posts.map((post) => (
            <div key={post.uri} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex gap-3">
                {post.author.avatar && (
                  <img
                    src={post.author.avatar}
                    alt={post.author.displayName}
                    className="w-10 h-10 rounded-full flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900 text-sm">
                      {post.author.displayName}
                    </span>
                    <span className="text-gray-500 text-sm">@{post.author.handle}</span>
                    <span className="text-gray-400 text-xs">{formatDate(post.createdAt)}</span>
                  </div>

                  <p className="text-gray-800 text-sm whitespace-pre-wrap mb-2">{post.text}</p>

                  {post.imageUrl && (
                    <img
                      src={post.imageUrl}
                      alt=""
                      className="rounded-lg max-h-64 object-cover mb-2"
                    />
                  )}

                  {post.url && (
                    <a
                      href={post.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline text-sm flex items-center gap-1"
                    >
                      {post.url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}

                  <div className="flex items-center gap-4 mt-2 text-gray-500 text-xs">
                    <span className="flex items-center gap-1">
                      <MessageCircle className="w-3.5 h-3.5" />
                      {post.engagement.replies}
                    </span>
                    <span className="flex items-center gap-1">
                      <Repeat2 className="w-3.5 h-3.5" />
                      {post.engagement.reposts}
                    </span>
                    <span className="flex items-center gap-1">
                      <Heart className="w-3.5 h-3.5" />
                      {post.engagement.likes}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && (!data?.posts || data.posts.length === 0) && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg shadow">
          <p>No posts found.</p>
          <p className="text-sm mt-2">Select some Bluesky handles above to see their posts.</p>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sources Editor (for drawer)
// ─────────────────────────────────────────────────────────────────────────────

function SourcesEditor({ onClose }: { onClose: () => void }) {
  const { data: sourcesData, isLoading } = useSources();
  const updateSource = useUpdateSource();
  const createSource = useCreateSource();
  const [editedUrls, setEditedUrls] = useState<Record<string, string>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSource, setNewSource] = useState({ name: '', homepage_url: '', sitemap_url: '' });

  const sources = sourcesData || [];

  const handleSave = async (sourceId: string) => {
    const url = editedUrls[sourceId];
    if (url === undefined) return;

    await updateSource.mutateAsync({
      id: sourceId,
      data: { sitemap_url: url || null },
    });

    setEditedUrls((prev) => {
      const next = { ...prev };
      delete next[sourceId];
      return next;
    });
  };

  const handleAddSource = async () => {
    if (!newSource.name || !newSource.homepage_url) return;

    await createSource.mutateAsync({
      name: newSource.name,
      homepage_url: newSource.homepage_url,
      sitemap_url: newSource.sitemap_url || undefined,
    });

    setNewSource({ name: '', homepage_url: '', sitemap_url: '' });
    setShowAddForm(false);
  };

  if (isLoading) {
    return <div className="text-gray-500 text-sm p-4">Loading sources...</div>;
  }

  return (
    <div className="space-y-4 pr-2">
      {!showAddForm ? (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full px-4 py-3 text-sm border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors"
        >
          + Add New Source
        </button>
      ) : (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 space-y-3">
          <div className="font-medium text-gray-900 text-sm">Add New Source</div>
          <input
            type="text"
            placeholder="Source Name (e.g., BBC News)"
            value={newSource.name}
            onChange={(e) => setNewSource((prev) => ({ ...prev, name: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
          <input
            type="url"
            placeholder="Homepage URL (e.g., https://bbc.com)"
            value={newSource.homepage_url}
            onChange={(e) => setNewSource((prev) => ({ ...prev, homepage_url: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
          <input
            type="url"
            placeholder="Sitemap/RSS URL (optional)"
            value={newSource.sitemap_url}
            onChange={(e) => setNewSource((prev) => ({ ...prev, sitemap_url: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAddSource}
              disabled={!newSource.name || !newSource.homepage_url || createSource.isPending}
              className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {createSource.isPending ? 'Adding...' : 'Add Source'}
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewSource({ name: '', homepage_url: '', sitemap_url: '' });
              }}
              className="px-3 py-2 text-sm border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {sources.length === 0 && !showAddForm && (
        <div className="text-gray-500 text-sm text-center py-4">
          No sources yet. Click above to add your first source.
        </div>
      )}

      {sources.map((source) => {
        const currentUrl = editedUrls[source.id] ?? source.sitemap_url ?? '';
        const hasChanges = editedUrls[source.id] !== undefined;

        return (
          <div key={source.id} className="border-b border-gray-100 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-medium text-gray-900">{source.name}</div>
                <div className="text-xs text-gray-500">{source.homepage_url}</div>
              </div>
              {source.sitemap_url && (
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                  Configured
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="url"
                placeholder="https://example.com/sitemap.xml or /rss.xml"
                value={currentUrl}
                onChange={(e) =>
                  setEditedUrls((prev) => ({ ...prev, [source.id]: e.target.value }))
                }
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
              {hasChanges && (
                <button
                  onClick={() => handleSave(source.id)}
                  disabled={updateSource.isPending}
                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateSource.isPending ? '...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
